import { useEffect, useRef, useState } from 'react';
import {
  CREATE_FEE,
  DEFAULT_SERVER_SIZE,
  formatMoney,
  GAME_DEFINITIONS,
  resolveGameSettings,
  SERVER_NAME,
  SERVER_NAME_MAX,
  SERVER_SIZES,
  SIZE_SPECS,
  sizePricePerHour,
  type GameSettings,
  type GameVersion,
  type GameVersionsResponse,
  type ListMyServersResponse,
  type MeResponse,
  type ServerOperationResult,
  type CreateUploadResponse,
  type MyBilling,
  type RedeemedCredit,
  type ServerSize,
  type CapacityResponse,
} from '@hearth/shared';
import type { Api } from '../api';
import { linkTo, navigate } from '../router';
import { Campfire } from '../ui/Campfire';
import { acceptTerms, methodText, openStripe } from './Billing';
import { choiceLabel, errorText, playText, rateText, sizeText, timeAgo } from './serverWords';
import { GrassBlockIcon, TreeIcon, UploadIcon } from './ChoiceIcons';
import { changedSettings, GameSettingsPanel, WorldOptions } from './GameOptions';
import { fileProblem, fileSize, sendForm, UploadCancelled, uploadedText, UploadError, waitForCheck, WorldRefused } from './upload';

// /app/new: create a server (M10.8), as the canvas board: game, name, world (with its options),
// game settings, size (M10.7), version, then a summary with "Create and wake". Bringing your own
// world (M10.16) uploads it, waits for the check, then creates the server from it.

const GAME = 'minecraft-java';
/** How many versions the list shows at first, and adds each time it's scrolled near the end. */
export const VERSION_PAGE = 12;
const NEW_DAYS = 30;

export type World = 'fresh' | 'upload';
/** Where creating from an upload has got to. */
export type Stage = 'uploading' | 'checking' | 'creating';

/** Why a name won't do, or '' if it will (the API's own rule: SERVER_NAME). */
export function nameProblem(name: string): string {
  if (!name.trim()) return 'Give it a name.';
  if ([...name].length > SERVER_NAME_MAX) return `At most ${SERVER_NAME_MAX} characters.`;
  if (!SERVER_NAME.test(name)) return 'No spaces at the start or end.';
  return '';
}

/** A release from the last 30 days is flagged New. */
export const isNew = (v: GameVersion, now: Date) => now.getTime() - Date.parse(v.releasedAt) < NEW_DAYS * 86_400_000;

/** "2 weeks ago" for recent releases, "March 2024" for older ones. */
export function releasedWhen(releasedAt: string, now: Date): string {
  if (now.getTime() - Date.parse(releasedAt) < 60 * 86_400_000) return timeAgo(releasedAt, now);
  return new Date(releasedAt).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

/** Where the page keeps its choices for the tab while Stripe takes a payment method (it comes back to /app/new). */
export const CREATE_DRAFT_KEY = 'hearth.createDraft';

interface Draft {
  name: string;
  version?: string | undefined;
  size: ServerSize;
  agreed: boolean;
  settings?: GameSettings;
}

/** The choices kept before going to Stripe, taken once (a file can't be kept: an upload is chosen again). */
function takeDraft(): Draft | undefined {
  try {
    const kept = sessionStorage.getItem(CREATE_DRAFT_KEY);
    sessionStorage.removeItem(CREATE_DRAFT_KEY);
    return kept ? (JSON.parse(kept) as Draft) : undefined;
  } catch {
    return undefined; // storage blocked: start afresh
  }
}

/**
 * What billing allows (B5): `ok` with what's charged, none on file, or unpaid invoices; with free credit,
 * `credit` (it covers the create fee: no card needed), `short` (some, not enough, no card) or `usedUp`
 * (none left, no card). Admins need none of it.
 */
export type Payment =
  | { state: 'ok'; method?: string }
  | { state: 'credit'; left: number; method?: string }
  | { state: 'short'; left: number }
  | { state: 'usedUp' }
  | { state: 'none' }
  | { state: 'pastDue' };

export function paymentOf(me: MeResponse, billing: MyBilling | undefined): Payment | undefined {
  if (me.admin) return { state: 'ok' };
  if (!billing) return undefined;
  if (billing.pastDue) return { state: 'pastDue' };
  const method = billing.paymentMethod ? methodText(billing.paymentMethod) : undefined;
  const credit = billing.credit;
  // Credit is spent first, so it pays the fee even with a card on file.
  if (credit && credit.left * 100 >= CREATE_FEE) return { state: 'credit', left: credit.left, ...(method ? { method } : {}) };
  if (method) return { state: 'ok', method };
  if (credit && credit.left > 0) return { state: 'short', left: credit.left };
  return credit ? { state: 'usedUp' } : { state: 'none' };
}

/** Whole hours of play at `size` that `leftCents` buys after the create fee. */
export const hoursAfterFee = (leftCents: number, size: ServerSize) => Math.max(0, Math.floor((leftCents * 100 - CREATE_FEE) / sizePricePerHour(size)));

/**
 * Whether the person may create one now: under their own limit, and Hearth under its server cap
 * (`full`). Admins pass both.
 */
export function mayCreate(me: MeResponse, owned: number, full = false): 'yes' | 'limit' | 'full' {
  if (me.admin) return 'yes';
  if (owned >= me.serverLimit) return 'limit';
  return full ? 'full' : 'yes';
}

export function CreatePage({ api, me }: { api: Api; me: MeResponse }) {
  const [draft] = useState(takeDraft);
  const [owned, setOwned] = useState<number>();
  const [versions, setVersions] = useState<GameVersion[]>();
  const [billing, setBilling] = useState<MyBilling>();
  /** Whether Hearth is at its server cap (asked only for non-admins: admins pass it). */
  const [full, setFull] = useState<boolean>();
  const [name, setName] = useState(draft?.name ?? '');
  const [version, setVersion] = useState<string | undefined>(draft?.version);
  const [size, setSize] = useState<ServerSize>(draft?.size ?? DEFAULT_SERVER_SIZE);
  const [settings, setSettings] = useState<GameSettings>(() => resolveGameSettings(GAME, draft?.settings));
  // One box for Hearth's Terms and the game's EULA: unticked until the owner ticks it, and required (the API
  // refuses a create without the EULA). The Terms are recorded on the account too, until they're agreed.
  const [agreed, setAgreed] = useState(draft?.agreed ?? false);
  const needsTerms = !me.admin && !me.termsAccepted;
  const [shown, setShown] = useState(VERSION_PAGE);
  const [touched, setTouched] = useState(false);
  const [world, setWorld] = useState<World>('fresh');
  const [file, setFile] = useState<File>();
  const [stage, setStage] = useState<Stage>();
  const [progress, setProgress] = useState(0);
  const [startedAt, setStartedAt] = useState(0);
  /** Why the world check refused the last upload: shown in the World step until another file is chosen. */
  const [refused, setRefused] = useState('');
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const cancel = useRef<AbortController>(undefined);
  const busy = stage !== undefined;

  // Leaving mid-upload would lose it: the browser asks first.
  useEffect(() => {
    if (!busy) return;
    const stay = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', stay);
    return () => window.removeEventListener('beforeunload', stay);
  }, [busy]);

  useEffect(() => {
    api.get<ListMyServersResponse>('/v1/servers').then((r) => setOwned(r.servers.filter((s) => s.role === 'owner').length), (err) => setError(errorText(err)));
    api.get<GameVersionsResponse>(`/v1/games/${GAME}/versions`).then((r) => setVersions(r.versions), (err) => setError(errorText(err)));
    if (!me.admin) api.get<MyBilling>('/v1/me/billing').then(setBilling, (err) => setError(errorText(err)));
    if (!me.admin) api.get<CapacityResponse>('/v1/servers/capacity').then((r) => setFull(r.full), (err) => setError(errorText(err)));
  }, [api, me.admin]);

  /** Keeps the choices for the tab, then goes to Stripe, which comes back here. */
  function addPayment() {
    setError('');
    try {
      sessionStorage.setItem(CREATE_DRAFT_KEY, JSON.stringify({ name, version, size, agreed, settings } satisfies Draft));
    } catch {
      // Storage blocked: the choices are made again after.
    }
    (async () => {
      if (needsTerms) await acceptTerms(api);
      await openStripe(api, 'card', '/app/new');
    })().catch((err: unknown) => setError(errorText(err)));
  }

  /** A free credit code, from the payment gate; billing is read again so the gate turns to the credit. */
  function redeem() {
    setRedeeming(true);
    setCodeError('');
    api
      .post<RedeemedCredit>('/v1/me/credit/redeem', { code })
      .then(async () => {
        setCode('');
        setBilling(await api.get<MyBilling>('/v1/me/billing'));
      })
      .catch((err: unknown) => setCodeError(errorText(err)))
      .finally(() => setRedeeming(false));
  }

  async function create() {
    setTouched(true);
    const chosen = version ?? versions?.[0]?.version;
    const uploading = world === 'upload';
    if (nameProblem(name) || !chosen || !agreed || (uploading && (!file || fileProblem(file)))) return;
    setError('');
    setRefused('');
    try {
      if (needsTerms) await acceptTerms(api);
      let upload: string | undefined;
      if (uploading && file) {
        setStage('uploading');
        setProgress(0);
        setStartedAt(Date.now());
        cancel.current = new AbortController();
        const form = await api.post<CreateUploadResponse>('/v1/uploads', { game: GAME });
        await sendForm(form, file, setProgress, cancel.current.signal);
        setStage('checking');
        await waitForCheck(api, form.uploadId);
        upload = form.uploadId;
      }
      setStage('creating');
      // Only what differs from the defaults; an uploaded world keeps its own world settings.
      const chosenSettings = changedSettings(GAME, settings, !uploading);
      const created = await api.post<ServerOperationResult>('/v1/servers', {
        name,
        game: GAME,
        version: chosen,
        size,
        acceptEula: true,
        ...(Object.keys(chosenSettings).length > 0 ? { settings: chosenSettings } : {}),
        ...(upload ? { upload } : {}),
      });
      navigate(`/app/servers/${encodeURIComponent(created.serverId)}`);
    } catch (err) {
      // A refused world is cleared, so trying again means choosing a file again (not re-sending this one).
      if (err instanceof WorldRefused) {
        setRefused(err.reason);
        setFile(undefined);
      } else if (!(err instanceof UploadCancelled)) {
        setError(err instanceof UploadError ? err.message : errorText(err));
      }
      setStage(undefined);
    }
  }

  return (
    <CreateView
      me={me}
      owned={owned}
      full={me.admin ? false : full}
      versions={versions}
      name={name}
      version={version ?? versions?.[0]?.version}
      size={size}
      settings={settings}
      onSettings={setSettings}
      agreed={agreed}
      onAgree={setAgreed}
      needsTerms={needsTerms}
      world={world}
      file={file}
      stage={stage}
      progress={progress}
      shown={shown}
      touched={touched}
      busy={busy}
      error={error}
      now={new Date()}
      onName={setName}
      onVersion={setVersion}
      onSize={setSize}
      onWorld={setWorld}
      onFile={(chosen) => {
        setFile(chosen);
        setRefused('');
      }}
      refused={refused}
      elapsedMs={stage === 'uploading' ? Date.now() - startedAt : 0}
      onCancel={() => cancel.current?.abort()}
      onMore={() => setShown((n) => n + VERSION_PAGE)}
      onCreate={() => void create()}
      payment={paymentOf(me, billing)}
      onAddPayment={addPayment}
      redeem={{ code, onCode: setCode, onRedeem: redeem, error: codeError, busy: redeeming }}
    />
  );
}

/** Why there's no form: one centred card, as the Connect Discord page's, back to the servers. */
function StopCard({ title, text }: { title: string; text: string }) {
  return (
    <section className="join-card" aria-live="polite">
      <span className="join-fire"><Campfire state="lit" height={70} /></span>
      <h2>{title}</h2>
      <p className="quiet">{text}</p>
      <a className="btn btn-primary btn-tall" {...linkTo('/app')}>Your servers</a>
    </section>
  );
}

/** The create page, given what's loaded and chosen (no fetching: easy to test). */
export function CreateView(props: {
  me: MeResponse;
  owned: number | undefined;
  /** Whether Hearth is at its server cap; undefined while it's read. */
  full: boolean | undefined;
  versions: GameVersion[] | undefined;
  name: string;
  version: string | undefined;
  size: ServerSize;
  /** The game's settings as chosen so far (every offered one, defaults included). */
  settings: GameSettings;
  onSettings: (settings: GameSettings) => void;
  /** Ticked the one box: Hearth's Terms and the game's EULA. */
  agreed: boolean;
  onAgree: (agreed: boolean) => void;
  /** Not yet agreed to Hearth's current Terms: adding a card records them, so it waits for the box. */
  needsTerms?: boolean | undefined;
  world: World;
  /** The file chosen to upload. */
  file: { name: string; size: number } | undefined;
  stage?: Stage;
  /** How much of the file has gone, 0–1, while uploading. */
  progress?: number;
  /** How long the upload has been going, for the time left. */
  elapsedMs?: number;
  /** Why the world check refused the last upload, if it did. */
  refused?: string;
  shown: number;
  touched: boolean;
  busy: boolean;
  error: string;
  now: Date;
  onName: (name: string) => void;
  onVersion: (version: string) => void;
  onSize: (size: ServerSize) => void;
  onWorld: (world: World) => void;
  onFile: (file: File | undefined) => void;
  onCancel?: () => void;
  onMore: () => void;
  onCreate: () => void;
  /** What billing allows (B5); undefined while it's read: no gate, the API still checks. */
  payment?: Payment | undefined;
  onAddPayment?: () => void;
  /** The code box in the payment gate, with no credit and no card. */
  redeem?: { code: string; onCode: (code: string) => void; onRedeem: () => void; error: string; busy: boolean } | undefined;
}) {
  const { me, owned, full, versions, name, version, size, settings, onSettings, agreed, onAgree, needsTerms, world, file, stage, progress = 0, elapsedMs = 0, refused = '', shown, touched, busy, error, now, onName, onVersion, onSize, onWorld, onFile, onCancel, onMore, onCreate, payment, onAddPayment, redeem } = props;
  const payFirst = payment !== undefined && ['none', 'pastDue', 'short', 'usedUp'].includes(payment.state);
  const uploading = world === 'upload';
  const badFile = file ? fileProblem(file) : touched && uploading ? "Choose your world's file." : '';
  const game = GAME_DEFINITIONS[GAME];
  const problem = touched ? nameProblem(name) : '';
  const allowed = owned === undefined || full === undefined ? undefined : mayCreate(me, owned, full);
  const list = versions?.slice(0, shown) ?? [];
  // An uploaded world keeps its own world settings, Hardcore included.
  const play = uploading ? { ...settings, hardcore: false } : settings;

  return (
    <>
      <a className="back" {...linkTo('/app')}>← Servers</a>
      <h1 className="page-title">New server</h1>
      {error && <p className="notice" role="alert">{error}</p>}
      {allowed === 'limit' ? (
        <StopCard title={`You have ${owned} servers already`} text="That's the most you can have for now." />
      ) : allowed === 'full' ? (
        <StopCard title="Sorry, Hearth is at max capacity right now" text="Please try again later." />
      ) : !allowed || !versions ? (
        !error && <p className="quiet" aria-live="polite">Loading…</p>
      ) : (
        <div className="server-panels">
          <div className="create-steps">
            <section className="panel">
              <h2>1 · Game</h2>
              {/* One game for now: shown chosen. A second (Hytale) makes these a real choice. */}
              <div className="choices">
                <div className="choice on with-icon">
                  <GrassBlockIcon />
                  <span>
                    <strong>{game.displayName}</strong>
                    <span className="quiet">{`Versions ${versions.at(-1)?.version ?? ''} to the newest`}</span>
                  </span>
                </div>
                <div className="choice choice-soon" aria-hidden="true">More games coming</div>
              </div>
            </section>
            <section className="panel">
              <h2><label htmlFor="server-name">2 · Name</label></h2>
              <input
                id="server-name"
                className="field"
                type="text"
                placeholder="Friday Night SMP"
                maxLength={SERVER_NAME_MAX}
                value={name}
                aria-invalid={problem ? 'true' : undefined}
                aria-describedby={problem ? 'name-problem' : undefined}
                onChange={(e) => onName(e.target.value)}
              />
              {problem && <p id="name-problem" className="field-problem">{problem}</p>}
            </section>
            <section className="panel">
              <h2 id="world-step">3 · World</h2>
              <div role="radiogroup" aria-labelledby="world-step" className="choices">
                <button className={uploading ? 'choice with-icon' : 'choice on with-icon'} type="button" role="radio" aria-checked={!uploading} disabled={busy} onClick={() => onWorld('fresh')}>
                  <TreeIcon />
                  <span>
                    <strong>A fresh world</strong>
                    <span className="quiet">Generate a brand new world</span>
                  </span>
                </button>
                <button className={uploading ? 'choice on with-icon' : 'choice with-icon'} type="button" role="radio" aria-checked={uploading} disabled={busy} onClick={() => onWorld('upload')}>
                  <UploadIcon />
                  <span>
                    <strong>Bring your world</strong>
                    <span className="quiet">Upload a save you already play</span>
                  </span>
                </button>
              </div>
              {!uploading && <WorldOptions game={GAME} settings={settings} onChange={onSettings} disabled={busy} />}
              {uploading && refused && (
                <p className="notice" role="alert">
                  {`Invalid World Upload: ${refused}`}
                </p>
              )}
              {uploading && (
                <div
                  className="dropzone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (!busy && e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]);
                  }}
                >
                  {file ? (
                    <p>
                      <strong>{file.name}</strong>
                      {` · ${fileSize(file.size)}`}
                    </p>
                  ) : (
                    <p>
                      Drop your world's <strong>.zip</strong> or <strong>.tar.gz</strong> here
                    </p>
                  )}
                  {!busy && (
                    <label className={file ? 'quiet-btn' : 'btn'}>
                      {file ? 'Choose another file' : 'Choose a file'}
                      <input className="sr-only" type="file" accept=".zip,.tar.gz,.tgz" onChange={(e) => onFile(e.target.files?.[0])} />
                    </label>
                  )}
                  {!file && <p className="quiet">The world folder (the one with level.dat), up to 4 GB.</p>}
                  {badFile && <p className="field-problem">{badFile}</p>}
                </div>
              )}
            </section>
            <GameSettingsPanel game={GAME} title="4 · Game settings" settings={play} onChange={onSettings} newWorld={!uploading} note="You can change the mode and difficulty later." disabled={busy} />
            <section className="panel">
              <h2 id="size-step">5 · How many players?</h2>
              <p className="quiet">You can change it later, while it's asleep.</p>
              <div role="radiogroup" aria-labelledby="size-step" className="choices sizes">
                {SERVER_SIZES.map((s) => (
                  <button key={s} className={s === size ? 'choice on' : 'choice'} type="button" role="radio" aria-checked={s === size} onClick={() => onSize(s)}>
                    <strong>{SIZE_SPECS[s].name}</strong>
                    <span className="quiet">{SIZE_SPECS[s].players}</span>
                    <span className="size-rate">{rateText(s)}</span>
                  </button>
                ))}
              </div>
            </section>
            <section className="panel">
              <h2>6 · Version</h2>
              <p className="quiet">
                {uploading ? "Pick the version your world was last played on, or a newer one. Friends' Minecraft needs to match it." : "The newest is picked for you. Friends' Minecraft needs to match it."}
              </p>
              <div
                role="radiogroup"
                aria-label="Minecraft version"
                className="versions"
                onScroll={(e) => {
                  const el = e.currentTarget;
                  if (shown < versions.length && el.scrollTop + el.clientHeight >= el.scrollHeight - 48) onMore();
                }}
              >
                {list.map((v) => (
                  <button key={v.version} className={v.version === version ? 'version on' : 'version'} type="button" role="radio" aria-checked={v.version === version} onClick={() => onVersion(v.version)}>
                    <span>
                      {`Minecraft ${v.version}`}
                      {isNew(v, now) && <span className="new">New</span>}
                    </span>
                    <span className="quiet">{releasedWhen(v.releasedAt, now)}</span>
                  </button>
                ))}
                <p className="quiet versions-note">{shown < versions.length ? 'Scroll for older versions.' : `That's every version back to ${versions.at(-1)?.version}.`}</p>
              </div>
            </section>
          </div>
          <aside className="panel create-summary">
            <h2>Your server</h2>
            <dl className="details">
              {[
                ['Game', game.displayName],
                ['Name', name.trim() || '—'],
                ['World', uploading ? (file?.name ?? 'Your upload') : `A fresh world · ${choiceLabel(GAME, 'worldType', settings.worldType)}`],
                ['Game mode', playText(GAME, play)],
                ['Size', sizeText(size)],
                ['Price', `${formatMoney(CREATE_FEE)} to create, then ${rateText(size)} while awake`],
                ...(payment?.state === 'credit' ? [['Free credit', `${formatMoney(payment.left * 100)} left`]] : []),
                ['Version', version ? `Minecraft ${version}` : '—'],
                ['Falls asleep', 'After 30 minutes with nobody on'],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            {stage === 'uploading' ? (
              <div className="upload-progress" aria-live="polite">
                <strong>Uploading your world</strong>
                <progress max={1} value={progress} aria-label="Uploading your world" />
                <p className="quiet">{uploadedText(progress, file?.size ?? 0, elapsedMs)}</p>
                <p>Please stay on this page until it's done.</p>
                {onCancel && (
                  <button className="quiet-btn" type="button" onClick={onCancel}>
                    Cancel upload
                  </button>
                )}
              </div>
            ) : stage === 'checking' ? (
              <p className="upload-wait" role="status">
                Validating your world files. Please stay on this page.
              </p>
            ) : (
              <>
                <label className="check">
                  <input type="checkbox" checked={agreed} disabled={busy} onChange={(e) => onAgree(e.target.checked)} aria-describedby={touched && !agreed ? 'eula-problem' : undefined} />
                  <span>
                    I agree to the{' '}
                    <a href="/terms" target="_blank" rel="noreferrer">
                      Terms
                    </a>{' '}
                    and the{' '}
                    <a href={game.eula.url} target="_blank" rel="noreferrer">
                      {game.eula.name}
                    </a>
                    .
                  </span>
                </label>
                {touched && !agreed && <p id="eula-problem" className="field-problem">Agree to the Terms and the {game.eula.name} to create your server.</p>}
                {payment?.state === 'credit' && (
                  <div className="pay-gate pay-gate-ok">
                    <strong>Paid from your free credit</strong>
                    <p className="quiet">
                      {`${formatMoney(CREATE_FEE)} to create and about ${hoursAfterFee(payment.left, size)} hours of play after it.`}
                      {!payment.method && " No card needed, so you can't be charged."}
                    </p>
                  </div>
                )}
                {(payment?.state === 'short' || payment?.state === 'usedUp') && (
                  <div className="pay-gate pay-gate-add">
                    <strong>{payment.state === 'short' ? 'Your credit won’t cover this one' : 'Your free credit is used up'}</strong>
                    <p className="quiet">
                      {payment.state === 'short'
                        ? `You have ${formatMoney(payment.left * 100)} of credit left, and creating a server costs ${formatMoney(CREATE_FEE)}. Add a payment method to create a server.`
                        : 'Add a payment method to create more servers.'}
                    </p>
                    <button className="btn btn-primary" type="button" disabled={needsTerms && !agreed} onClick={onAddPayment}>Add a payment method</button>
                  </div>
                )}
                {payment?.state === 'none' && (
                  <div className="pay-gate pay-gate-add">
                    <strong>Add a payment method</strong>
                    <p className="quiet">With a card, you're charged on the 1st of each month for the time your servers were awake.</p>
                    <button className="btn btn-primary" type="button" disabled={needsTerms && !agreed} onClick={onAddPayment}>Add a payment method</button>
                    {redeem && (
                      <>
                        <p className="pay-gate-or">or</p>
                        <form
                          className="billing-code"
                          onSubmit={(e) => {
                            e.preventDefault();
                            if (redeem.code.trim()) redeem.onRedeem();
                          }}
                        >
                          <label className="pay-gate-label" htmlFor="create-code">Have a code?</label>
                          <input
                            id="create-code"
                            className="field"
                            placeholder="Code"
                            value={redeem.code}
                            autoComplete="off"
                            aria-invalid={redeem.error ? true : undefined}
                            aria-describedby={redeem.error ? 'create-code-problem' : undefined}
                            onChange={(e) => redeem.onCode(e.target.value)}
                          />
                          <button className="btn" type="submit" disabled={redeem.busy || !redeem.code.trim()}>Redeem</button>
                          {redeem.error && <p id="create-code-problem" className="field-problem" role="alert">{redeem.error}</p>}
                        </form>
                      </>
                    )}
                  </div>
                )}
                {payment?.state === 'pastDue' && (
                  <div className="pay-gate pay-gate-due" role="alert">
                    <strong>You have unpaid invoices</strong>
                    <p>Update your payment method in Billing to create servers.</p>
                    <a className="btn" {...linkTo('/account/billing')}>Go to Billing</a>
                  </div>
                )}
                {payment?.state === 'ok' && payment.method && <p className="quiet">{`Charged to ${payment.method} on the 1st of each month.`}</p>}
                <button className="btn btn-primary btn-tall" type="button" disabled={busy || payFirst} onClick={onCreate}>
                  {busy ? 'Creating…' : 'Create and wake'}
                </button>
              </>
            )}
          </aside>
        </div>
      )}
    </>
  );
}
